/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import type { PaginatedResponse, User as BaseUser } from './types'
import { authApiService } from './auth'

// Extend the base User interface to include properties that might be added by transformations
export interface Users {
  id: number
  firstName: string | null
  lastName: string | null
  fullName: string
  role: string
  email: string
  enabled: boolean
}

export interface User extends BaseUser {
  name?: string
  chName?: string
  systemHelperType?: string
  _uid?: string
}

export interface Group {
  id: number
  name: string
  type?: string
  systemHelperType?: string
  _uid?: string
  chName?: string
}

export interface UserPickerOption {
  value: number
  label: string
}

export interface UserPickerResponse {
  options: UserPickerOption[]
  hasMore: boolean
}

export interface AssembledUsersGroups {
  mergedAll: (User | Group)[]
  merged: (User | Group)[]
  usersAll: User[]
  users: User[]
  usersDisabled: User[]
  groups: Group[]
  options?: UserPickerOption[]
  hasMore?: boolean
}

export interface UserRole {
  id: string
  value: string
}

export interface UserFilter {
  sortBy?: string
  sortDesc?: boolean
  enabled?: boolean
  name?: string
  pageStart?: number
  pageSize?: number
  [key: string]: string | number | boolean | undefined
}

export interface EmailData {
  message: string
  subject: string
  userIdsTo: number | number[]
  [key: string]: string | number | number[]
}

export interface UserGroupsUpdate {
  groupIds: number[]
  createRATasksNow: boolean
  [key: string]: number[] | boolean
}

export interface UserDisableRequest {
  assignTasksTo?: number
  [key: string]: number | undefined
}

export interface BulkUserRequest {
  ids: number[]
  assignTasksTo?: number
  [key: string]: number[] | number | undefined
}

export interface ExportUsersRequest {
  sorting: Record<string, unknown>
  filter: UserFilter
  filterId?: string
  [key: string]: Record<string, unknown> | UserFilter | string | undefined
}

// Constants for user roles (matching the original Constants.user.roles)
const UserRoles = {
  ADMIN: 'ADMIN',
  CONTENT_ADMIN: 'CONTENT_ADMIN',
  POWER_USER: 'POWER_USER',
  USER: 'USER',
  AUDITOR: 'AUDITOR',
  INSPECTOR: 'INSPECTOR',
  EXTERNAL: 'EXTERNAL',
  CLOUD_ADMIN_ROLE: 'CLOUD_ADMIN_ROLE',
  STUDY_ADMIN: 'STUDY_ADMIN'
} as const

// Constants for system helper types (matching the original Constants.user.systemHelperType)
const SystemHelperType = {
  USER: 'USER',
  GROUP: 'GROUP'
} as const

export class UsersApiService {
  private readonly baseURL = 'api/users'
  private readonly baseURL_v2 = 'api/v2/users'

  // Cache management (matching the original implementation)
  private availableUsers: User[] | null = null
  private availableGroups: Group[] | null = null
  private assembly: AssembledUsersGroups | null = null
  private defAssembly: Promise<AssembledUsersGroups> | null = null

  async getAllUsers(params: { sortBy: string; sortDesc: boolean }): Promise<PaginatedResponse<Users>> {
    return baseApiService.fetch<PaginatedResponse<Users>>('/api/users/all', {
      params
    })
  }

  // List and search methods
  async getList(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    return baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL_v2}`, {
      method: 'GET',
      params: filter
    })
  }

  // Deprecated, will be removed with angularjs
  async getForPicker(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    return baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL_v2}/picker`, {
      method: 'GET',
      params: filter
    })
  }

  async getOptionsForAsyncPicker(
    searchTerm?: string,
    pageStart: number = 0,
    additionalParams: Record<string, unknown> = {}
  ): Promise<UserPickerResponse> {
    const params: UserFilter = {
      pageStart,
      pageSize: 25,
      ...additionalParams
    }

    if (searchTerm) {
      params.name = searchTerm
    }

    const [response, policies] = await Promise.all([
      baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL_v2}/picker`, { params }),
      authApiService.getUserPolicies()
    ])

    const hasMore = response.elements.length > 0 && (pageStart + 1) * (params.pageSize || 25) < response.totalElements

    return {
      options: response.elements
        .map((user) => ({
          value: user.id,
          label: (user.name || user.fullName) + (policies.showEmailAddress ? ` (${user.email})` : '')
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
      hasMore
    }
  }

  // Helper method for loading users groups (matches original loadUsersGroups)
  loadUsersGroups(): void {
    this.getAssembledUsersGroups(true)
  }

  // Individual user methods
  // Get user by ID from cached users (matches original getUserById)
  getUserById(userId: number): User {
    if (this.availableUsers) {
      const user = this.availableUsers.find((user) => user.id === userId)
      if (user) return user
    }

    // Return placeholder user if not found (matches original behavior)
    // Using a simplified version that matches the base User interface
    return {
      id: userId,
      firstName: 'NOT_AVAILABLE', // This would be translated in the original
      lastName: '',
      fullName: 'NOT_AVAILABLE',
      accountName: '',
      email: '',
      role: '',
      groupIds: [],
      permissions: null,
      enabled: false,
      lastAccessAt: 0,
      hasValidPasswordResetToken: false,
      locked: false,
      mobileAppAccess: false,
      expiresAt: null,
      title: null,
      department: null,
      locale: '',
      authType: '',
      timeFormat24h: false,
      dateFormat: '',
      timeFormat: '',
      serviceAccount: false,
      accountType: '',
      canCreateReports: false,
      canCreateActivityReports: false,
      tenantUuid: '',
      ssomanaged: false,
      roleEditable: false,
      eosSponsorPowerUserPlus: false
    }
  }

  async exportUsersReports(filter: UserFilter, meta: Record<string, unknown>, filterId?: string): Promise<void> {
    const reqObj: ExportUsersRequest = { sorting: meta, filter }
    if (filterId) {
      reqObj.filterId = filterId
    }

    return baseApiService.fetch('/api/v2/reports/userexport', {
      method: 'POST',
      body: reqObj
    })
  }

  async getUsersReports(params: UserFilter): Promise<unknown> {
    return baseApiService.fetch('/api/v2/reports/userreport', {
      method: 'GET',
      params: { ...params }
    })
  }

  // Helper methods
  getFullName(firstName: string, lastName: string): string {
    return `${firstName} ${lastName}`
  }

  getDisplayName(fullName: string, email: string): string {
    return `${fullName} (${email})`
  }

  // Transform users to add name and chName properties based on policies
  async transformUsers(users: User[]): Promise<User[]> {
    const policies = await authApiService.getUserPolicies()

    if (policies.showEmailAddress) {
      // If showing email addresses, use displayName format for all users
      users.forEach((user) => {
        const displayName = this.getDisplayName(user.fullName, user.email)
        user.chName = displayName
        user.name = displayName
      })
    } else {
      // Group users by fullName to handle duplicates
      const usersByFullName = users.reduce(
        (acc, user) => {
          if (!acc[user.fullName]) {
            acc[user.fullName] = []
          }
          acc[user.fullName].push(user)
          return acc
        },
        {} as Record<string, User[]>
      )

      // Process each group
      Object.values(usersByFullName).forEach((fullNameGroup) => {
        fullNameGroup.forEach((user) => {
          const displayName =
            fullNameGroup.length > 1
              ? `${user.fullName} (${user.email})` // Append email if duplicate fullNames
              : user.fullName // Use just fullName if unique

          user.chName = displayName
          user.name = displayName
        })
      })
    }

    return users
  }

  // Private method to assemble users and groups with metadata (matches original _assembleUsersGroups)
  private _assembleUsersGroups(): AssembledUsersGroups {
    const users = (this.availableUsers || []).map((item) => ({
      ...item,
      systemHelperType: SystemHelperType.USER,
      _uid: `${SystemHelperType.USER}-${item.id}`
    }))

    const groups = (this.availableGroups || []).map((item) => ({
      ...item,
      systemHelperType: SystemHelperType.GROUP,
      _uid: `${SystemHelperType.GROUP}-${item.id}`,
      chName: item.name,
      name: item.name
    }))

    const usersEnabled = users.filter((user) => user.enabled)
    const usersDisabled = users.filter((user) => !user.enabled)

    this.assembly = {
      mergedAll: [...users, ...groups],
      merged: [...usersEnabled, ...groups],
      usersAll: users,
      users: usersEnabled,
      usersDisabled,
      groups
    }

    return this.assembly
  }

  // Load groups from API (matches original loadGroups)
  private async loadGroups(): Promise<void> {
    try {
      // Match original: ApiGroups.getAllForDropdownsWithType(true, {sortBy: 'name', sortDesc: false})
      // Using groups API call with hideLoader=true behavior
      const response = await baseApiService.fetch<PaginatedResponse<Group>>('/api/groups/allwithtype', {
        params: { sortBy: 'name', sortDesc: false }
      })
      this.availableGroups = response.elements || []
    } catch (error) {
      console.error('Failed to load groups:', error)
      this.availableGroups = []
    }
  }

  // Update loadUsers to properly cache the result (matches original)
  private async loadUsersInternal(): Promise<void> {
    // Match original: getAllForDropdowns(true, {sortBy: 'lastName', sortDesc: false})
    const response = await this.getAllForDropdowns({ sortBy: 'lastName', sortDesc: false })
    this.availableUsers = await this.transformUsers(response.elements || [])
  }

  // Utility methods for loading users (with proper cache management)
  async loadUsers(): Promise<PaginatedResponse<User>> {
    // Match original: getAllForDropdowns(true, {sortBy: 'lastName', sortDesc: false})
    const response = await this.getAllForDropdowns({ sortBy: 'lastName', sortDesc: false })
    // Transform users to add name and chName properties, just like the original
    response.elements = await this.transformUsers(response.elements)
    return response
  }

  // Assembled users groups method with proper caching logic (matches original getAssembledUsersGroups)
  async getAssembledUsersGroups(refreshFromServer?: boolean): Promise<AssembledUsersGroups> {
    if (!refreshFromServer && this.assembly) {
      // Return cached assembly if available and not refreshing
      return Promise.resolve(this.assembly)
    } else {
      if (this.defAssembly) {
        return this.defAssembly
      } else {
        this.defAssembly = (async () => {
          try {
            const promises: Promise<void>[] = []

            // Load users if needed
            if (refreshFromServer || !this.availableUsers) {
              promises.push(this.loadUsersInternal())
            }

            // Load groups if needed
            if (refreshFromServer || !this.availableGroups) {
              promises.push(this.loadGroups())
            }

            await Promise.all(promises)

            const result = this._assembleUsersGroups()
            this.defAssembly = null
            return result
          } catch (error) {
            console.log('assembling users groups failed', error)
            this.defAssembly = null
            throw error
          }
        })()

        return this.defAssembly
      }
    }
  }

  async sendEmail(userId: number | number[], subject: string, message: string): Promise<void> {
    const data: EmailData = {
      message,
      subject,
      userIdsTo: userId
    }

    return baseApiService.fetch('/api/emails', {
      method: 'POST',
      body: data
    })
  }

  // User groups methods
  async getUserGroups(userId: number): Promise<PaginatedResponse<Group>> {
    return baseApiService.fetch<PaginatedResponse<Group>>(`/${this.baseURL}/${userId}/groups`)
  }

  // Roles and reports methods
  async getAvailableRoles(): Promise<UserRole[]> {
    // Implementation matching the original getAvailableRoles function
    const possibleRoles: UserRole[] = []

    const roleKeys = [
      UserRoles.ADMIN,
      UserRoles.CONTENT_ADMIN,
      UserRoles.POWER_USER,
      UserRoles.USER,
      UserRoles.AUDITOR,
      UserRoles.INSPECTOR,
      UserRoles.EXTERNAL,
      UserRoles.CLOUD_ADMIN_ROLE,
      UserRoles.STUDY_ADMIN
    ]

    roleKeys.forEach((roleKey) => {
      possibleRoles.push({
        id: roleKey,
        value: `ROLE_${roleKey}` // In a real implementation, this would use a translation service
      })
    })

    return possibleRoles
  }

  private async getByStatus(enabledUsers: boolean, filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const statusFilter = {
      sortBy: 'lastName',
      sortDesc: false,
      ...filter,
      enabled: enabledUsers
    }

    const response = await baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL}`, {
      params: statusFilter
    })

    // Transform users to add name and chName properties, just like the original
    response.elements = await this.transformUsers(response.elements)
    return response
  }

  async getEnabledAll(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const filterWithEnabled = { ...filter, enabled: true }
    return this.getByStatus(true, filterWithEnabled)
  }

  async getAllForDropdowns(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const defaultFilter = { sortBy: 'lastName', sortDesc: false, ...filter }
    return baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL}/all`, {
      params: defaultFilter
    })
  }

  async getAll(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const defaultFilter = { sortBy: 'lastName', sortDesc: false, ...filter }
    return baseApiService.fetch<PaginatedResponse<User>>(`/${this.baseURL}`, {
      params: defaultFilter
    })
  }

  async getDisabledAll(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const filterWithEnabled = { ...filter, enabled: false }
    return this.getByStatus(false, filterWithEnabled)
  }

  async getById(userId: number): Promise<User> {
    return baseApiService.fetch<User>(`/${this.baseURL}/${userId}`)
  }

  // User management methods
  async create(userData: Partial<User>): Promise<User> {
    return baseApiService.fetch<User>(`/${this.baseURL}`, {
      method: 'POST',
      body: userData
    })
  }

  async update(id: number, userData: Partial<User>): Promise<User> {
    return baseApiService.fetch<User>(`/${this.baseURL}/${id}`, {
      method: 'PUT',
      body: userData
    })
  }

  async updateGroups(userId: number, groupsIds: number[], createRATasksNow?: boolean): Promise<void> {
    const groupsObj: UserGroupsUpdate = {
      groupIds: groupsIds,
      createRATasksNow: createRATasksNow || false
    }

    return baseApiService.fetch(`/${this.baseURL}/${userId}/groups`, {
      method: 'PATCH',
      body: groupsObj
    })
  }

  async disable(userId: number, assignTasksTo?: number): Promise<void> {
    const reqObj: UserDisableRequest = {}
    if (assignTasksTo) {
      reqObj.assignTasksTo = assignTasksTo
    }

    return baseApiService.fetch(`/${this.baseURL}/${userId}/disable`, {
      method: 'PATCH',
      body: reqObj
    })
  }

  async bulkActivate(userIds: number[]): Promise<void> {
    const reqObj: BulkUserRequest = { ids: userIds }
    return baseApiService.fetch(`/${this.baseURL}/enable`, {
      method: 'POST',
      body: reqObj
    })
  }

  async bulkDeactivate(userIds: number[], assignTasksTo?: number): Promise<void> {
    const reqObj: BulkUserRequest = { ids: userIds }
    if (assignTasksTo) {
      reqObj.assignTasksTo = assignTasksTo
    }

    return baseApiService.fetch(`/${this.baseURL}/disable`, {
      method: 'POST',
      body: reqObj
    })
  }

  // Email and notification methods
  async resendWelcomeMail(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/resendWelcome`, {
      method: 'PATCH',
      body: {}
    })
  }

  //This api is not used anywhere in the react codebase
  async resendIosWelcomeMail(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/resendWelcomeIos`, {
      method: 'PATCH'
    })
  }

  async enable(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/enable`, {
      method: 'PATCH'
    })
  }

  async unlock(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/unlock`, {
      method: 'PATCH'
    })
  }

  // Security and password methods
  async invalidateUserResetPswToken(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/invalidateToken`, {
      method: 'PATCH'
    })
  }

  async disconnectFromOktaAndResetPwd(userId: number): Promise<void> {
    return baseApiService.fetch(`/${this.baseURL}/${userId}/disconnectFromOktaAndResetPwd`, {
      method: 'PATCH'
    })
  }
}

export const usersApiService = new UsersApiService()
